import { useState, FormEvent } from 'react';
import { ImageUploader } from '../ui/ImageUploader';
import { convertToAVIF, getPreferredExtension } from '../../lib/imageUtils';
import { apiUrl } from '../../lib/api';

interface ExerciseFormProps {
	selectedDate: string;
	onSuccess?: (message: string) => void;
}

type SessionSubTab = 'morning' | 'afternoon';

const sessionSubTabs: { id: SessionSubTab; label: string }[] = [
	{ id: 'morning', label: 'Pagi' },
	{ id: 'afternoon', label: 'Sore' },
];

/**
 * ExerciseForm Component
 *
 * Form for logging exercise entries with session type.
 * Posts to /api/exercise endpoint.
 */
export function ExerciseForm({ selectedDate, onSuccess }: ExerciseFormProps) {
	const [activeSubTab, setActiveSubTab] = useState<SessionSubTab>('morning');
	const [exerciseType, setExerciseType] = useState('');
	const [duration, setDuration] = useState('');
	const [notes, setNotes] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [pendingBlobs, setPendingBlobs] = useState<Blob[]>([]);
	const [uploaderKey, setUploaderKey] = useState(0);

	const resetForm = () => {
		setExerciseType('');
		setDuration('');
		setNotes('');
		setPendingBlobs([]);
		setUploaderKey((k) => k + 1);
	};

	const uploadImages = async (entryId: string, blobs: Blob[]) => {
		const ext = await getPreferredExtension();
		for (const blob of blobs) {
			const result = await convertToAVIF(blob);

			const formData = new FormData();
			formData.append('file', result.blob, `image-${Date.now()}.${ext}`);
			formData.append('entryType', 'exercise');
			formData.append('entryId', entryId);
			formData.append('width', result.width.toString());
			formData.append('height', result.height.toString());

			const res = await fetch(apiUrl('/api/images'), {
				method: 'POST',
				credentials: 'include',
				body: formData,
			});
			if (!res.ok) {
				throw new Error('Gagal mengunggah foto. Silakan coba lagi.');
			}
		}
	};

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		setIsSubmitting(true);
		setError(null);

		// Validate exercise type
		if (!exerciseType.trim()) {
			setError('Jenis olahraga harus diisi');
			setIsSubmitting(false);
			return;
		}

		// Validate duration
		const durationValue = parseInt(duration, 10);
		if (isNaN(durationValue) || durationValue <= 0) {
			setError('Durasi harus berupa angka positif');
			setIsSubmitting(false);
			return;
		}

		try {
			const res = await fetch(apiUrl('/api/exercise'), {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({
					date: selectedDate,
					sessionType: activeSubTab,
					exerciseType: exerciseType.trim(),
					durationMinutes: duration,
					notes: notes || undefined,
				}),
			});

			if (!res.ok) {
				const data = await res.json();
				throw new Error(data.error || 'Gagal menyimpan data');
			}

			const data = await res.json();

			// Upload images if any
			if (pendingBlobs.length > 0) {
				await uploadImages(data.id, pendingBlobs);
			}

			const tabLabel = sessionSubTabs.find((t) => t.id === activeSubTab)?.label;
			onSuccess?.(`Olahraga ${tabLabel} berhasil dicatat!`);
			resetForm();
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Terjadi kesalahan');
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-5">
			<h2 className="text-lg font-semibold text-gray-900">Catat Olahraga</h2>

			{/* Sub-tabs for session type */}
			<div className="flex gap-2 border-b border-gray-200 pb-2">
				{sessionSubTabs.map((tab) => (
					<button
						key={tab.id}
						type="button"
						onClick={() => setActiveSubTab(tab.id)}
						className={`px-4 py-2.5 min-h-11 text-sm font-medium rounded-full transition ${
							activeSubTab === tab.id
								? 'bg-blue-100 text-blue-700'
								: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
						}`}
					>
						{tab.label}
					</button>
				))}
			</div>

			{/* Error Display */}
			{error && (
				<div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
					{error}
				</div>
			)}

			{/* Exercise Type Input */}
			<div>
				<label
					htmlFor="exercise-type"
					className="block text-sm font-medium text-gray-700 mb-2"
				>
					Jenis Olahraga <span className="text-red-500">*</span>
				</label>
				<div className="flex flex-wrap gap-2">
					<input
						type="text"
						id="exercise-type"
						value={exerciseType}
						onChange={(e) => setExerciseType(e.target.value)}
						placeholder="Contoh: Jalan kaki, Bersepeda"
						required
						disabled={isSubmitting}
						className="flex-1 min-w-[150px] px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100 disabled:cursor-not-allowed"
					/>
					<div className="flex gap-1">
						{['Angkat Barbel', 'Senam'].map((type) => (
							<button
								key={type}
								type="button"
								onClick={() => setExerciseType(type)}
								disabled={isSubmitting}
								className="px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{type}
							</button>
						))}
					</div>
				</div>
			</div>

			{/* Duration Input */}
			<div>
				<label
					htmlFor="exercise-duration"
					className="block text-sm font-medium text-gray-700 mb-2"
				>
					Durasi (menit) <span className="text-red-500">*</span>
				</label>
				<input
					type="number"
					id="exercise-duration"
					value={duration}
					onChange={(e) => setDuration(e.target.value)}
					min="1"
					placeholder="Contoh: 30"
					required
					disabled={isSubmitting}
					className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100 disabled:cursor-not-allowed"
				/>
			</div>

			{/* Notes Input */}
			<div>
				<label
					htmlFor="exercise-notes"
					className="block text-sm font-medium text-gray-700 mb-2"
				>
					Catatan (opsional)
				</label>
				<textarea
					id="exercise-notes"
					value={notes}
					onChange={(e) => setNotes(e.target.value)}
					rows={3}
					placeholder="Catatan tambahan..."
					disabled={isSubmitting}
					className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
				/>
			</div>

			{/* Image Upload Section (inline) */}
			<div>
				<label className="block text-sm font-medium text-gray-700 mb-2">
					Foto (opsional)
				</label>
				<ImageUploader
					key={uploaderKey}
					entryType="exercise"
					onPendingBlobsChange={setPendingBlobs}
					disabled={isSubmitting}
				/>
			</div>

			{/* Submit Button - Sticky on mobile for easy access */}
			<div className="sticky bottom-0 pt-4 -mx-6 px-6 pb-1 bg-white sm:static sm:mx-0 sm:px-0 sm:pb-0 sm:bg-transparent">
				<button
					type="submit"
					disabled={isSubmitting}
					className="w-full bg-blue-600 text-white py-3 px-4 min-h-12 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition font-medium shadow-lg sm:shadow-none"
				>
					{isSubmitting ? 'Menyimpan...' : 'Simpan'}
				</button>
			</div>
		</form>
	);
}
